// ─────────────────────────────────────────────────────────────────────────────
// TradeReview — matured version
//
// Replaces the hardcoded if/else "Pattern Recognition" with real analysis
// that contextualizes the last trade against the trader's full history.
//
// Sections:
//   1. Trade selector — pick any recent trade, not just the last one
//   2. Trade card — symbol, direction, P&L, time, entry/exit prices
//   3. Execution scorecard — discipline, conviction, focus, fear, rule adherence
//      each compared to the trader's personal baseline
//   4. Context flags — was this trade during a known bad pattern?
//      (Hour Cliff worst slot, revenge window, worst setup, worst mental state)
//   5. Recent trades strip — last 10 trades for streak context
//   6. Coaching note — specific, data-driven, not canned
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useDetectors } from "../../hooks/useDetectors";
import { TradeShareModal } from "../landing/TradeShareModal";

function tradeNet(t) {
  return (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
}

function tradeGross(t) {
  return Number(t.pnl) || 0;
}

function avg(arr) {
  if (!arr.length) return null;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

// ─── Metric bar ────────────────────────────────────────────────────────────────
function MetricBar({ label, value, baseline, max = 10, higherIsBetter = true }) {
  if (value == null) return null;
  const pct     = Math.min(100, (value / max) * 100);
  const bPct    = baseline != null ? Math.min(100, (baseline / max) * 100) : null;
  const isBetter = higherIsBetter ? value >= (baseline || 5) : value <= (baseline || 5);
  const color   = isBetter ? C.green : C.red;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: C.muted }}>{label}</span>
        <span style={{ fontFamily: "monospace", color, fontWeight: 700 }}>
          {value.toFixed(1)}
          {baseline != null && (
            <span style={{ color: C.muted, fontWeight: 400 }}> / avg {baseline.toFixed(1)}</span>
          )}
        </span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "visible", position: "relative" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: color, borderRadius: 3,
          transition: "width 0.3s ease",
        }} />
        {bPct != null && (
          <div style={{
            position: "absolute", top: -3, left: `${bPct}%`,
            width: 2, height: 12,
            background: C.muted, borderRadius: 1,
            transform: "translateX(-50%)",
          }} title={`Your average: ${baseline?.toFixed(1)}`} />
        )}
      </div>
    </div>
  );
}

// ─── Context flag ──────────────────────────────────────────────────────────────
function ContextFlag({ icon, title, detail, severity = "warn" }) {
  const color = severity === "good" ? C.green : severity === "warn" ? C.yellow : C.red;
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "flex-start",
      padding: "10px 12px",
      background: color + "0d",
      border: `1px solid ${color}30`,
      borderRadius: 8,
      marginBottom: 8,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color }}>{title}</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{detail}</div>
      </div>
    </div>
  );
}

// ─── Mini trade row ────────────────────────────────────────────────────────────
function MiniTradeRow({ trade, isSelected, onClick, pnlFn }) {
  const net = pnlFn ? pnlFn(trade) : tradeNet(trade);
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "52px 60px 32px 1fr 76px",
        gap: 8, alignItems: "center",
        padding: "8px 10px",
        background: isSelected ? C.blue + "15" : "#0b0d19",
        border: `1px solid ${isSelected ? C.blue : C.border + "40"}`,
        borderRadius: 6, cursor: "pointer",
        fontSize: 12,
        transition: "background 0.1s",
      }}
    >
      <span style={{ color: C.muted, fontFamily: "monospace", fontSize: 11 }}>
        {(trade.entry_time || trade.trade_time || "").slice(0, 5) || trade.trade_date?.slice(5)}
      </span>
      <span style={{ color: C.text, fontWeight: 700 }}>{trade.symbol || "—"}</span>
      <span style={{ color: trade.direction === "Long" ? C.green : C.red, fontWeight: 700, fontSize: 11 }}>
        {trade.direction === "Long" ? "↑" : "↓"}
      </span>
      <span style={{ color: C.muted, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {(trade.setup_type || trade.entry_signal || "—").replace(/_/g, " ")}
      </span>
      <span style={{
        color: net >= 0 ? C.green : C.red,
        fontWeight: 700, fontFamily: "monospace", textAlign: "right",
      }}>
        {fmt$(net)}
      </span>
    </div>
  );
}

// ─── Coaching note builder ─────────────────────────────────────────────────────
function buildCoachingNote(trade, baselines, contextFlags, recentTrades) {
  const notes = [];
  const net = tradeNet(trade);

  // Emotional state analysis vs baseline
  if (trade.fear_level != null && baselines.avgFear != null) {
    if (trade.fear_level >= 7 && trade.fear_level > baselines.avgFear + 1.5) {
      notes.push(net >= 0
        ? `Fear was elevated (${trade.fear_level}/10 vs your avg ${baselines.avgFear.toFixed(1)}) but you traded through it profitably — strong mental discipline.`
        : `Fear was elevated (${trade.fear_level}/10 vs your avg ${baselines.avgFear.toFixed(1)}) and the trade lost. High-fear entries are a pattern worth watching.`
      );
    }
  }

  if (trade.impulsiveness != null && baselines.avgImpulsiveness != null) {
    if (trade.impulsiveness >= 7 && trade.impulsiveness > baselines.avgImpulsiveness + 1.5) {
      notes.push(net < 0
        ? `Impulsiveness was high (${trade.impulsiveness}/10). This trade lost — worth reviewing whether the entry met your criteria or was reactive.`
        : `Impulsiveness was high (${trade.impulsiveness}/10) but resulted in a win. Don't let this reinforce impulsive behavior — luck and skill look the same in the moment.`
      );
    }
  }

  // Conviction vs outcome
  if (trade.conviction_level != null) {
    if (trade.conviction_level <= 4 && net >= 0) {
      notes.push(`Low conviction (${trade.conviction_level}/10) trade that worked out. Consider whether this was luck or a genuine edge — if you wouldn't take this setup again, that's your answer.`);
    } else if (trade.conviction_level >= 8 && net < 0) {
      notes.push(`High conviction (${trade.conviction_level}/10) trade that lost. This happens — but if it repeats, your conviction calibration may need review. Check the Conviction Calibration detector in Psychology.`);
    }
  }

  // Rule adherence
  if (trade.rule_adherence != null && baselines.avgRuleAdherence != null) {
    if (trade.rule_adherence < baselines.avgRuleAdherence - 2) {
      notes.push(`Rule adherence (${trade.rule_adherence}/10) was below your average (${baselines.avgRuleAdherence.toFixed(1)}). Review what rule you bent — consistency here compounds over hundreds of trades.`);
    } else if (trade.rule_adherence >= 9) {
      notes.push(`Strong rule adherence (${trade.rule_adherence}/10). This is what professional trading looks like — the outcome is secondary to the process.`);
    }
  }

  // Context flags
  const badFlags = contextFlags.filter(f => f.severity === "bad");
  if (badFlags.length > 0 && net < 0) {
    notes.push(`This trade triggered ${badFlags.length} known pattern flag${badFlags.length > 1 ? "s" : ""} from your history. The loss may not be random — it fits an existing behavioral pattern.`);
  }

  // Recent streak context
  const recentNets = recentTrades.slice(0, 5).map(tradeNet);
  const recentLosses = recentNets.filter(n => n < 0).length;
  if (recentLosses >= 3 && net < 0) {
    notes.push(`You've taken ${recentLosses} losses in your last 5 trades. This is the revenge-trading window — consider stepping back before the next entry.`);
  } else if (recentNets.slice(0, 4).every(n => n > 0) && net > 0) {
    notes.push(`4+ consecutive profitable trades heading into this one — a strong streak. Watch for win-streak euphoria causing oversizing on the next entry.`);
  }

  // Post-trade emotion
  if (trade.post_trade_emotion === "Euphoric" && net > 0) {
    notes.push(`Post-trade emotion: Euphoric. This is exactly when discipline gets tested on the next entry — euphoria after a win is a known precursor to oversizing.`);
  } else if (trade.post_trade_emotion === "Angry" && net < 0) {
    notes.push(`Post-trade emotion: Angry. Take a break before the next trade. Anger after a loss is the most reliable predictor of revenge trading.`);
  }

  // Default if nothing specific
  if (notes.length === 0) {
    if (net >= 0 && (trade.rule_adherence || 5) >= 7) {
      notes.push("Clean execution. You followed your process and the trade worked. Keep this as the standard.");
    } else if (net < 0 && (trade.rule_adherence || 5) >= 7) {
      notes.push("The process was right even though the outcome was negative. A loss with good discipline is still a good trade. Focus on the next one.");
    } else {
      notes.push("Log emotional fields (fear, focus, impulsiveness, conviction) on your next trade to unlock deeper trade-by-trade analysis.");
    }
  }

  return notes;
}

// ─── Build context flags from detector data and trade fields ──────────────────
function buildContextFlags(trade, allTrades, detectorResults) {
  const flags = [];
  if (!trade || !allTrades.length) return flags;

  // Hour Cliff: is this trade in the worst slot?
  const hourCliff = detectorResults?.detectors?.find(d => d.id === "hour_cliff");
  if (hourCliff && hourCliff.confidence !== "insufficient" && trade.entry_time) {
    const worstSlot = hourCliff.details?.worst_slot?.slot;
    if (worstSlot) {
      const [sH, sM] = worstSlot.split(":").map(Number);
      const [tH, tM] = String(trade.entry_time).split(":").map(Number);
      const slotStart = sH * 60 + sM;
      const tradeTime = tH * 60 + tM;
      if (tradeTime >= slotStart && tradeTime < slotStart + 30) {
        flags.push({
          icon: "⏰",
          title: `Worst Hour Slot: ${worstSlot}`,
          detail: `Your Hour Cliff detector shows ${worstSlot} has only ${Math.round((hourCliff.details?.worst_slot?.win_rate || 0) * 100)}% win rate across ${hourCliff.details?.worst_slot?.n || "?"} trades. This trade entered in that exact window.`,
          severity: "bad",
        });
      }
    }
  }

  // Revenge window: was there a loss within 5 minutes before this trade?
  const sortedByTime = [...allTrades]
    .filter(t => t.trade_date === trade.trade_date && t.id !== trade.id)
    .sort((a, b) => (a.entry_time || "").localeCompare(b.entry_time || ""));

  const thisIdx = sortedByTime.findIndex(t => t.entry_time === trade.entry_time);
  const prevTrade = thisIdx > 0 ? sortedByTime[thisIdx - 1] : null;
  if (prevTrade && tradeNet(prevTrade) < 0) {
    const [pH, pM] = String(prevTrade.entry_time || "00:00").split(":").map(Number);
    const [tH, tM] = String(trade.entry_time || "00:00").split(":").map(Number);
    const gapMinutes = (tH * 60 + tM) - (pH * 60 + pM);
    if (gapMinutes >= 0 && gapMinutes <= 10) {
      flags.push({
        icon: "⚡",
        title: `Revenge Trading Window`,
        detail: `This trade was placed ${gapMinutes} minute${gapMinutes !== 1 ? "s" : ""} after a losing trade. Your Revenge Trading detector has flagged this pattern before.`,
        severity: "bad",
      });
    }
  }

  // Setup edge: is this a net-negative setup?
  const setupEdge = detectorResults?.detectors?.find(d => d.id === "setup_edge");
  if (setupEdge && trade.setup_type && setupEdge.details?.leaking_setups?.length) {
    const isLeaking = setupEdge.details.leaking_setups.some(s => s.name === trade.setup_type);
    if (isLeaking) {
      const setupData = setupEdge.details.leaking_setups.find(s => s.name === trade.setup_type);
      flags.push({
        icon: "📋",
        title: `Net-Negative Setup: ${trade.setup_type?.replace(/_/g, " ")}`,
        detail: `Your data shows this setup averages ${fmt$(setupData?.avg_net || 0)}/trade across ${setupData?.n || "?"} trades. Consider whether this setup belongs in your playbook.`,
        severity: "bad",
      });
    }
  }

  // Mental state: is this a known bad state?
  const mentalState = detectorResults?.detectors?.find(d => d.id === "mental_state");
  if (mentalState && trade.mental_state && mentalState.details?.worst) {
    if (trade.mental_state === mentalState.details.worst.state && mentalState.details.worst.avg_net < 0) {
      flags.push({
        icon: "🧠",
        title: `Worst Mental State: ${trade.mental_state}`,
        detail: `Your Psychology data shows "${trade.mental_state}" is your worst-performing mental state (avg ${fmt$(mentalState.details.worst.avg_net)}/trade). This trade was placed in that state.`,
        severity: "bad",
      });
    }
  }

  // Good flags
  if (trade.rule_adherence >= 9) {
    flags.push({
      icon: "✓",
      title: "High Rule Adherence",
      detail: `Rule adherence of ${trade.rule_adherence}/10 — this trade met your own standards regardless of outcome.`,
      severity: "good",
    });
  }

  if (trade.conviction_level >= 8 && tradeNet(trade) > 0) {
    flags.push({
      icon: "🎯",
      title: "High Conviction Win",
      detail: `Conviction ${trade.conviction_level}/10 with a profitable outcome — this is the pattern to trade more.`,
      severity: "good",
    });
  }

  return flags;
}

// ─────────────────────────────────────────────────────────────────────────────
// TradeReview — main export
// ─────────────────────────────────────────────────────────────────────────────
export function TradeReview({ trades, setView, accounts = [], currentAccountId, setCurrentAccountId, userId }) {
  const accountTrades = useMemo(() =>
    currentAccountId
      ? trades.filter(t => t.account_id === currentAccountId)
      : trades,
    [trades, currentAccountId]
  );

  const recentTrades = useMemo(() => {
    const brokerSort = (a, b) => {
      if (a.exited_at && b.exited_at) {
        const diff = new Date(b.exited_at) - new Date(a.exited_at);
        if (diff !== 0) return diff;
        const bidA = Number(a.broker_trade_id) || 0;
        const bidB = Number(b.broker_trade_id) || 0;
        return bidA - bidB;
      }
      const realDate = (t) => {
        const s = t.trade_date || t.created_at?.split("T")[0] || "";
        const h = parseInt((t.exit_time || t.entry_time || "00").slice(0, 2), 10);
        if (s && !isNaN(h) && h >= 17) {
          const d = new Date(s + "T12:00:00"); d.setDate(d.getDate() - 1);
          return d.toISOString().slice(0, 10);
        }
        return s;
      };
      const dA = `${realDate(a)} ${a.exit_time || a.entry_time || "00:00"}`;
      const dB = `${realDate(b)} ${b.exit_time || b.entry_time || "00:00"}`;
      if (dB !== dA) return dB.localeCompare(dA);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    };
    return [...accountTrades].sort(brokerSort).slice(0, 20);
  }, [accountTrades]);

  const [selectedId, setSelectedId] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [pnlMode, setPnlMode] = useState("net");
  const selectedTrade = selectedId
    ? recentTrades.find(t => t.id === selectedId) || recentTrades[0]
    : recentTrades[0];

  // Detector results — fetched server-side via the run-detectors Edge Function.
  // `suite: "all"` is required because buildContextFlags reads detectors from
  // multiple suites: hour_cliff + setup_edge (behavior) and mental_state
  // (psychology). We flatten into a single { detectors: [...] } shape so
  // buildContextFlags keeps consuming `detectorResults?.detectors?.find(...)`
  // unchanged.
  const { data: allSuites } = useDetectors({
    suite: "all",
    trades: accountTrades,
    userId,
  });

  const detectorResults = useMemo(() => {
    if (!allSuites || accountTrades.length < 10) return null;
    return {
      detectors: [
        ...(allSuites.behavior?.detectors    || []),
        ...(allSuites.risk?.detectors        || []),
        ...(allSuites.context?.detectors     || []),
        ...(allSuites.psychology?.detectors  || []),
      ],
    };
  }, [allSuites, accountTrades.length]);

  // Compute baselines from all trades
  const baselines = useMemo(() => {
    const withFields = accountTrades.filter(t => t.fear_level != null);
    return {
      avgFear:          avg(withFields.map(t => t.fear_level).filter(Boolean)),
      avgFocus:         avg(accountTrades.filter(t => t.focus_level != null).map(t => t.focus_level)),
      avgImpulsiveness: avg(accountTrades.filter(t => t.impulsiveness != null).map(t => t.impulsiveness)),
      avgConviction:    avg(accountTrades.filter(t => t.conviction_level != null).map(t => t.conviction_level)),
      avgRuleAdherence: avg(accountTrades.filter(t => t.rule_adherence != null).map(t => t.rule_adherence)),
      avgDiscipline:    avg(accountTrades.filter(t => t.discipline_score != null).map(t => t.discipline_score)),
    };
  }, [accountTrades]);

  const contextFlags = useMemo(() =>
    selectedTrade ? buildContextFlags(selectedTrade, accountTrades, detectorResults) : [],
    [selectedTrade, accountTrades, detectorResults]
  );

  const coachingNotes = useMemo(() =>
    selectedTrade ? buildCoachingNote(selectedTrade, baselines, contextFlags, recentTrades) : [],
    [selectedTrade, baselines, contextFlags, recentTrades]
  );

  if (!recentTrades.length) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
        No trades logged yet.
      </div>
    );
  }

  const tradePnl = (t) => pnlMode === "net" ? tradeNet(t) : tradeGross(t);

  const t = selectedTrade;
  const net = t ? tradePnl(t) : 0;

  // Discipline score: prefer the logged discipline_score directly.
  // Only compute from sub-fields if 2+ are present — otherwise a single
  // default value of 5 from seeded trades would always show as "5".
  const subFields = [
    t.rule_adherence,
    t.conviction_level,
    t.focus_level ?? null,
    t.impulsiveness != null ? 10 - t.impulsiveness : null,
  ].filter(v => v != null);

  const computedScore = t.discipline_score != null && subFields.length === 0
    ? t.discipline_score  // only logged field — show it directly
    : subFields.length >= 2
    ? Math.round(avg([...subFields, ...(t.discipline_score != null ? [t.discipline_score] : [])]))
    : subFields.length === 1 && t.discipline_score != null
    ? Math.round(avg([subFields[0], t.discipline_score]))
    : null;  // not enough data — don't show a misleading score

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 4px" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>Trade Review</h2>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>
          Contextualizes each trade against your behavioral history
        </p>
        <div style={{ display: "flex", gap: 4 }}>
          {["gross", "net"].map(m => (
            <button key={m} onClick={() => setPnlMode(m)} style={{
              padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              border: `1px solid ${pnlMode === m ? C.blue : C.border}`,
              background: pnlMode === m ? C.blue + "22" : "transparent",
              color: pnlMode === m ? C.blue : C.muted,
              cursor: "pointer", textTransform: "uppercase",
            }}>{m}</button>
          ))}
        </div>
      </div>

      {/* Account selector */}
      {accounts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
          <select
            value={currentAccountId || ""}
            onChange={e => setCurrentAccountId(e.target.value)}
            style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
          >
            <option value="" disabled>Select an account…</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Trade selector — last 20 trades */}
      <Card style={{ marginBottom: 16, padding: "12px 14px" }}>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
          Recent Trades — select to review
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflowY: "auto" }}>
          {recentTrades.map(trade => (
            <MiniTradeRow
              key={trade.id}
              trade={trade}
              isSelected={trade.id === (selectedId || recentTrades[0]?.id)}
              onClick={() => setSelectedId(trade.id)}
              pnlFn={tradePnl}
            />
          ))}
        </div>
      </Card>

      {t && (
        <>
          {/* ── Trade summary card ───────────────────────────────────────── */}
          <Card style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>
                  {t.symbol} {t.direction} • {t.trade_date}
                  {t.entry_time && <span> • {String(t.entry_time).slice(0, 5)}</span>}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, color: net >= 0 ? C.green : C.red, fontFamily: "monospace" }}>
                  {fmt$(net)}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {pnlMode === "gross" ? "Gross P&L" : "Net P&L"}
                  {pnlMode === "gross" && ((Number(t.fees)||0) + (Number(t.commissions)||0)) > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      (fees {fmt$(-((Number(t.fees)||0) + (Number(t.commissions)||0)))} → net {fmt$(tradeNet(t))})
                    </span>
                  )}
                </div>
                {(t.entry_price || t.exit_price) && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontFamily: "monospace" }}>
                    {t.entry_price && `Entry ${t.entry_price}`}
                    {t.entry_price && t.exit_price && " → "}
                    {t.exit_price && `Exit ${t.exit_price}`}
                    {t.position_size && ` • ${t.position_size} contracts`}
                  </div>
                )}
              </div>
              {computedScore != null && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Discipline
                  </div>
                  <div style={{ fontSize: 44, fontWeight: 900, color: computedScore >= 7 ? C.green : computedScore >= 5 ? C.yellow : C.red, fontFamily: "monospace" }}>
                    {computedScore}
                  </div>
                  <button
                    onClick={() => setShareTarget(t)}
                    style={{ marginTop: 8, background: "#0d2b1f", border: "1px solid #1a4731", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 600, color: "#4caf7d", cursor: "pointer" }}
                  >
                    ↑ Share
                  </button>
                </div>
              )}
            </div>

            {/* Setup + exit reason */}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {t.setup_type && (
                <span style={{ fontSize: 11, padding: "3px 10px", background: C.purple + "22", border: `1px solid ${C.purple}40`, borderRadius: 12, color: C.purple }}>
                  {t.setup_type.replace(/_/g, " ")}
                </span>
              )}
              {t.exit_reason && (
                <span style={{ fontSize: 11, padding: "3px 10px", background: C.blue + "15", border: `1px solid ${C.blue}30`, borderRadius: 12, color: C.blue }}>
                  {t.exit_reason.replace(/_/g, " ")}
                </span>
              )}
              {t.mental_state && (
                <span style={{ fontSize: 11, padding: "3px 10px", background: C.muted + "15", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted }}>
                  🧠 {t.mental_state}
                </span>
              )}
              {t.market_condition && (
                <span style={{ fontSize: 11, padding: "3px 10px", background: C.muted + "15", border: `1px solid ${C.border}`, borderRadius: 12, color: C.muted }}>
                  📊 {t.market_condition}
                </span>
              )}
            </div>
          </Card>

          {/* ── Execution scorecard ──────────────────────────────────────── */}
          {[t.fear_level, t.focus_level, t.impulsiveness, t.conviction_level, t.rule_adherence].some(v => v != null) && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
                Execution Scorecard
                <span style={{ color: C.muted, fontWeight: 400, marginLeft: 8 }}>
                  — bar shows your personal baseline
                </span>
              </div>
              <MetricBar label="Rule Adherence"  value={t.rule_adherence}   baseline={baselines.avgRuleAdherence}  higherIsBetter />
              <MetricBar label="Conviction"      value={t.conviction_level} baseline={baselines.avgConviction}     higherIsBetter />
              <MetricBar label="Focus"           value={t.focus_level}      baseline={baselines.avgFocus}          higherIsBetter />
              <MetricBar label="Fear"            value={t.fear_level}       baseline={baselines.avgFear}           higherIsBetter={false} />
              <MetricBar label="Impulsiveness"   value={t.impulsiveness}    baseline={baselines.avgImpulsiveness}  higherIsBetter={false} />
              {t.energy_level != null && (
                <MetricBar label="Energy" value={t.energy_level} baseline={null} higherIsBetter />
              )}
            </Card>
          )}

          {/* ── Context flags ────────────────────────────────────────────── */}
          {contextFlags.length > 0 && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
                Pattern Context
              </div>
              {contextFlags.map((flag, i) => (
                <ContextFlag key={i} {...flag} />
              ))}
            </Card>
          )}

          {/* ── Coaching notes ───────────────────────────────────────────── */}
          <Card style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
              Coaching Notes
            </div>
            {coachingNotes.map((note, i) => (
              <div key={i} style={{
                fontSize: 13, color: C.text, lineHeight: 1.7,
                paddingBottom: i < coachingNotes.length - 1 ? 10 : 0,
                marginBottom: i < coachingNotes.length - 1 ? 10 : 0,
                borderBottom: i < coachingNotes.length - 1 ? `1px solid ${C.border}20` : "none",
              }}>
                {note}
              </div>
            ))}
          </Card>

          {/* ── Post-trade reflection ─────────────────────────────────────── */}
          {(t.post_trade_reflection || t.what_to_improve) && (
            <Card style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
                Your Notes
              </div>
              {t.post_trade_reflection && (
                <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6, marginBottom: t.what_to_improve ? 10 : 0, fontStyle: "italic" }}>
                  "{t.post_trade_reflection}"
                </div>
              )}
              {t.what_to_improve && (
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  <strong style={{ color: C.text }}>To improve:</strong> {t.what_to_improve}
                </div>
              )}
            </Card>
          )}
        </>
      )}
      {shareTarget && (
        <TradeShareModal
          trade={shareTarget}
          allTrades={accountTrades}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}
